import React, { type ReactNode, useEffect, useMemo } from 'react'
import { useCollectionData } from 'react-firebase-hooks/firestore'

import { captureException } from '@sentry/react'
import { query, where } from 'firebase/firestore'

import { piloteIdToLetter } from '@/model/utils/PiloteIdToLetter.tsx'

import {
  transportRequestCollection,
  type TransportRequestSnapshotData,
} from '../firestore/transportRequestCollection.ts'

import { type Team, useTeams } from './useTeams.ts'

export type TransportRequest = Omit<TransportRequestSnapshotData, 'team'> & {
  readonly letters: string
  readonly team?: Team
}

export interface TransportRequestsContextValue {
  readonly loading: boolean
  readonly transportRequests: ReadonlyArray<TransportRequest>
}

export const TransportRequestsContext = React.createContext<TransportRequestsContextValue>({
  transportRequests: [],
  loading: true,
})

export interface TransportRequestsProviderProps {
  regionId: string
  eveningId: number
  children: ReactNode
}

export const TransportRequestsProvider: React.FC<TransportRequestsProviderProps> = ({
  regionId,
  eveningId,
  children,
}) => {
  const { teams } = useTeams()
  const transportRequestsQuery = query(
    transportRequestCollection(),
    where('regionId', '==', regionId),
    where('eveningId', '==', eveningId),
    where('status', 'not-in', ['draft', 'deleted_draft', 'submitted', 'error'])
  )
  const [snapshotData = [], loading, error] = useCollectionData<TransportRequestSnapshotData>(transportRequestsQuery)
  useEffect(() => {
    if (error) {
      captureException(error, {
        tags: { 'firestore.query.snapshot': 'transportRequests' },
        fingerprint: ['firestore', 'query', 'transportRequests'],
      })
    }
  }, [error])

  const value = useMemo(
    () => ({
      transportRequests: snapshotData.map((transportRequest) => ({
        ...transportRequest,
        team: transportRequest.team && teams.find((team) => team._id === transportRequest.team?.id),
        letters: piloteIdToLetter(transportRequest.piloteId),
      })),
      loading,
    }),
    [snapshotData, teams, loading]
  )

  return <TransportRequestsContext.Provider value={value}>{children}</TransportRequestsContext.Provider>
}
